import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}
export function DateRangePicker({
  value,
  onChange
}: DateRangePickerProps) {
  const {
    t
  } = useTranslation("analytics");
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(value.start);
  const [tempEnd, setTempEnd] = useState(value.end);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const quickRanges = [{
    label: 'Today',
    days: 'today'
  }, {
    label: 'Yesterday',
    days: 'yesterday'
  }, {
    label: 'Last 7 days',
    days: 7
  }, {
    label: 'Last 4 weeks',
    days: 28
  }, {
    label: 'Last 6 months',
    days: 180
  }, {
    label: 'Last 12 months',
    days: 365
  }, {
    label: 'Month to date',
    days: 'mtd'
  }, {
    label: 'Quarter to date',
    days: 'qtd'
  }, {
    label: 'Year to date',
    days: 'ytd'
  }, {
    label: 'All time',
    days: 'all'
  }];
  const handleQuickRange = (days: number | string) => {
    const end = new Date();
    let start = new Date();
    if (typeof days === 'number') {
      start.setDate(end.getDate() - days);
    } else if (days === 'today') {
      start = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    } else if (days === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      end.setDate(end.getDate() - 1);
    } else if (days === 'mtd') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (days === 'qtd') {
      const quarter = Math.floor(end.getMonth() / 3);
      start = new Date(end.getFullYear(), quarter * 3, 1);
    } else if (days === 'ytd') {
      start = new Date(end.getFullYear(), 0, 1);
    } else if (days === 'all') {
      start = new Date(2020, 0, 1);
    }
    setTempStart(start);
    setTempEnd(end);
    setIsSelectingEnd(false);
  };
  const handleApply = () => {
    onChange({
      start: tempStart,
      end: tempEnd
    });
    setIsOpen(false);
    setIsSelectingEnd(false);
  };
  const handleClear = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    setTempStart(start);
    setTempEnd(end);
    setIsSelectingEnd(false);
  };
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const renderCalendar = (monthOffset: number) => {
    const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(displayMonth);
    const firstDay = getFirstDayOfMonth(displayMonth);
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" data-id="element-1569" />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
      const isInRange = date >= tempStart && date <= tempEnd;
      const isStart = date.toDateString() === tempStart.toDateString();
      const isEnd = date.toDateString() === tempEnd.toDateString();
      days.push(<button key={day} onClick={() => {
        if (!isSelectingEnd) {
          // First click: select start date
          setTempStart(date);
          setTempEnd(date);
          setIsSelectingEnd(true);
        } else {
          // Second click: select end date
          if (date < tempStart) {
            // If selected date is before start, swap them
            setTempEnd(tempStart);
            setTempStart(date);
          } else {
            setTempEnd(date);
          }
          setIsSelectingEnd(false);
        }
      }} className={`h-8 w-8 rounded-full text-sm flex items-center justify-center ${isStart || isEnd ? 'bg-blue-600 text-white font-medium' : isInRange ? 'bg-blue-100 text-blue-900' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1570">
          
          {day}
        </button>);
    }
    return <div className="flex-1" data-id="element-1571">
        <div className="flex items-center justify-between mb-2" data-id="element-1572">
          {monthOffset === 0 && <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-1 hover:bg-gray-100 rounded" data-id="element-1573">
            
              <ChevronLeftIcon size={16} data-id="element-1574" />
            </button>}
          <div className="text-sm font-medium" data-id="element-1575">
            {displayMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          })}
          </div>
          {monthOffset === 1 && <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-1 hover:bg-gray-100 rounded" data-id="element-1576">
            
              <ChevronRightIcon size={16} data-id="element-1577" />
            </button>}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-1578">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="h-8 flex items-center justify-center text-xs text-gray-500 font-medium" data-id="element-1579">
            
              {day}
            </div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-id="element-1580">{days}</div>
      </div>;
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1581">
      <button onClick={() => {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setIsSelectingEnd(false);
      }
    }} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1582">
        
        <CalendarIcon size={16} data-id="element-1583" />
        <span data-id="element-1584">
          {formatDate(value.start)} - {formatDate(value.end)}
        </span>
      </button>

      {isOpen && <div className="absolute left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50" style={{
      width: '800px'
    }} data-id="element-1585">
        
          <div className="flex gap-4" data-id="element-1586">
            {/* Quick ranges */}
            <div className="w-40 border-r border-gray-200 pr-4" data-id="element-1587">
              <div className="space-y-1" data-id="element-1588">
                {quickRanges.map(range => <button key={range.label} onClick={() => handleQuickRange(range.days)} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded" data-id="element-1589">
                
                    {range.label}
                  </button>)}
              </div>
            </div>

            {/* Calendars */}
            <div className="flex-1" data-id="element-1590">
              <div className="flex gap-4 mb-4" data-id="element-1591">
                {renderCalendar(0)}
                {renderCalendar(1)}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200" data-id="element-1592">
                <button onClick={handleClear} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md" data-id="element-1593">
                
                  {t("components.ComparisonSelector.clear")}
                </button>
                <button onClick={handleApply} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-1594">
                
                  {t("components.content.apply")}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}